import React from 'react';
// template components
import Accordion from '@nmx/template/dist/componentLibrary/modules/Accordion';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// icons
import MinusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/MinusSignFill.js';
import PlusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/PlusSignFill.js';

export default () => (
  <SectionContainer
    id='disability-insurance-faq'
    moduleName='faq-module'>
    <Row align='center'>
      <Col
        align='center'
        medium={10}
        large={8}>
        <Typography
          component='h2'
          nextElement='typography'>
          Questions about disability insurance&#63; <WidowBlock>We&#39;ve got answers.</WidowBlock>
        </Typography>
      </Col>
      <Col
        medium={10}
        large={8}>
        <Accordion
          id='faq-disability-insurance'
          variant='secondary'
          toggleOpenIcon={PlusSignFillIcon}
          toggleCloseIcon={MinusSignFillIcon}
          isFAQ
          items={[
            {
              id: 'disability-insurance-what-is-it',
              title: <>I&#39;m looking for a disability insurance definition&#8212;what is <WidowBlock>it exactly&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    Disability insurance can replace a portion of your income if you&#39;re ever too sick or injured to work. Having the right policy in place will help you cover expenses and keep your financial plan on track so you can focus on getting better. It&#39;s important to know how a provider defines a disability as it will help you figure out the right level of coverage you might need. Talk to one of our advisors to learn more.{' '}
                    <Link
                      id='faq-disability-insurance-match-with-fr-link'
                      linkUrl='/find-a-financial-advisor/'
                      variant='primaryDeemphasize'
                      ariaLabel='Get matched with a financial advisor today.'>
                      Get matched today
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'disability-insurance-why-do-i-need-it',
              title: <>Why do I need <WidowBlock>disability insurance&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    If you depend on your income, it&#39;s important to protect it. Disability insurance can close the gap between the coverage you have through your employer&#39;s group policy and what you&#39;ll need to maintain your lifestyle if you get sick or injured and can&#39;t work.{' '}
                    <Link
                      id='faq-disability-insurance-see-coverage-you-need-link'
                      linkUrl='/disability-insurance/disability-insurance-calculator/'
                      ariaLabel='See how much disability insurance coverage you need.'
                      variant='primaryDeemphasize'>
                      See how much coverage you need
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'disability-insurance-work-coverage',
              title: <>My company provides disability insurance. Isn&#39;t <WidowBlock>that enough&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    You might think you don&#39;t need disability insurance if you have coverage through work. But in most cases, that only covers around 50&#45;60&#37; of your income &#40;according to the US Bureau of Labor Statistics, National Compensation Survey: Employee Benefits in the United States, March 2021&#41;. Plus, you&#39;ll have to pay taxes on any payments you get from that coverage. But when you have your own policy, you can get closer to having 100&#37; of your income covered, the payments you get will not be taxed, and you&#39;ll stay covered even if you change jobs.{' '}
                    <Link
                      id='faq-disability-insurance-calculate-coverage-amount-link'
                      linkUrl='/disability-insurance/disability-insurance-calculator/'
                      ariaLabel='Calculate your disability insurance coverage amount.'
                      variant='primaryDeemphasize'>
                      Calculate your coverage amount
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'disability-insurance-social-security',
              title: <>Wouldn&#39;t I get disability insurance benefits from <WidowBlock>Social Security&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    There is a disability insurance component to Social Security. But the benefit payments are likely to be lower and qualifying for benefits can be more difficult than with a private policy.{' '}
                    <Link
                      id='faq-disability-insurance-connect-with-advisor-link'
                      linkUrl='/find-a-financial-advisor/'
                      ariaLabel='Connect with an advisor to discuss Social Security disability insurance benefits.'
                      variant='primaryDeemphasize'>
                      Connect with an advisor
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'disability-insurance-vs-workers-comp',
              title: <>Is disability insurance different from <WidowBlock>workers&#39; comp&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    Yes. While both will help you out financially if you&#39;re unable to work, they are used for different situations. Workers&#39; compensation is a government&#45;regulated program offered through most employers that provides benefits for a work&#45;related injury or illness (e.g. you hurt your back in the warehouse). Disability insurance on the other hand, is typically individual coverage that will help replace lost income if you&#39;re unable to work due to an injury or illness outside of work (e.g. a serious car accident).
                  </Typography>
                  <Typography disableBottomPadding>
                    Want to know more&#63; Read our article on the{' '}
                    <Link
                      id='faq-disability-insurance-vs-workers-comp-article-link'
                      linkUrl='/life-and-money/whats-the-difference-between-workers-comp-and-disability-insurance/'
                      ariaLabel='Find out the difference between workers compand disability insurance.'
                      variant='primaryDeemphasize'>
                      differences between workers&#39; comp and disability insurance.
                    </Link>
                  </Typography>
                </>
              ),
            },
            {
              id: 'disability-insurance-is-it-affordable',
              title: <>What does disability <WidowBlock>insurance cost&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    Disability insurance is generally quite affordable. In fact, most policies cost less than a daily cup of coffee.<sup>2</sup> When you buy a disability insurance policy, you pay a premium based on the length and type of coverage you choose, your health, the benefit amount, and other factors.{' '}
                    <Link
                      id='faq-disability-insurance-connect-with-advisor-cost-link'
                      linkUrl='/find-a-financial-advisor/'
                      ariaLabel='Connect with an advisor to discuss disability insurance costs.'
                      variant='primaryDeemphasize'>
                      Connect with an advisor
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'disability-insurance-eligibility',
              title: <>Who is eligible <WidowBlock>for coverage&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    Northwestern Mutual&#39;s disability insurance covers more people than ever before, including stay&#45;at&#45;home parents, caregivers, and part&#45;time employees.{' '}
                    <Link
                      id='faq-disability-insurance-how-it-can-protect-you-link'
                      linkUrl='/find-a-financial-advisor/'
                      ariaLabel='Connect with an advisor to discuss disability insurance costs.'
                      variant='primaryDeemphasize'>
                      Talk to an advisor
                    </Link>
                    {' '}about how disability insurance can help protect you.
                  </Typography>
                </>
              ),
            },
            {
              id: 'disability-insurance-how-does-it-work',
              title: <>What are the main types of disability insurance and how do <WidowBlock>they work&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    There are two types of disability insurance policies, short&#45;term and long&#45;term.{' '}
                    <Link
                      id='faq-disability-insurance-how-short-term-insurance-works'
                      linkUrl='/life-and-money/what-is-short-term-disability-insurance/'
                      ariaLabel='Learn about how Short&#45;Term Disability Insurance works.'
                      variant='primaryDeemphasize'>
                      Short&#45;term disability (STD)
                    </Link>
                    {' '}is usually provided by your employer and is designed to replace a portion of your income for a short period of time, like several months.
                  </Typography>
                  <Typography nextElement='typography'>
                    <Link
                      id='faq-disability-insurance-how-long-term-insurance-works'
                      linkUrl='/life-and-money/long-term-disability-insurance/'
                      ariaLabel='Learn about Long&#45;Term Disability Insurance.'
                      variant='primaryDeemphasize'>
                      Long&#45;term disability (LTD)
                    </Link>,
                    {' '}which we offer, gives you coverage if you&#39;re out of work for a longer period of time because of a more severe or permanent disability. LTD is sometimes offered as a workplace benefit, but it&#39;s typically purchased as an individual policy. Coverage is designed to last for many years&#8212;even through retirement if needed.
                  </Typography>
                  <Typography nextElement='typography'>
                    LTD and STD policies work best in tandem because LTD doesn&#39;t kick in right away and there&#39;s an elimination period, while an STD policy kicks in more immediately.
                  </Typography>
                  <Typography disableBottomPadding>
                    Ask one of our advisors if a long&#45;term disability policy is right for you.{' '}
                    <Link
                      id='faq-disability-insurance-get-matched-how-does-it-work-link'
                      linkUrl='/find-a-financial-advisor/'
                      ariaLabel='Connect with an advisor to discuss LTD and STD policies.'
                      variant='primaryDeemphasize'>
                      Get matched today
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'disability-insurance-types-of-disability-insurance',
              title: <>What kind of disability insurance can I get from <WidowBlock>Northwestern Mutual&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    For individuals, we offer{' '}
                    <Link
                      id='faq-disability-insurance-long-term-disability-insurance-link'
                      linkUrl='/life-and-money/long-term-disability-insurance/'
                      ariaLabel='Learn more about the kind of disability insurance you can get from Northwestern Mutual.'
                      variant='primaryDeemphasize'>
                      long&#45;term disability insurance
                    </Link>
                    {' '}that can help protect your income by replacing a portion of your paycheck if you&#39;re ever too sick or injured to work.
                  </Typography>
                  <Typography nextElement='typography'>
                    For business owners, we offer{' '}
                    <Link
                      id='faq-disability-insurance-group-disability-link'
                      linkUrl='/financial-planning/business-risk-management/'
                      ariaLabel='Learn more about group disability for business owners.'
                      variant='primaryDeemphasize'>
                      group disability
                    </Link>
                    {' '}that can help cover your overhead expenses and keep your business running if you or another key person becomes too sick or injured to do their job. It&#39;s also a valuable benefit you can offer employees to help attract and retain top talent.
                  </Typography>
                  <Typography disableBottomPadding>
                    An advisor can help you figure out how much coverage you&#39;ll need.{' '}
                    <Link
                      id='faq-disability-insurance-connect-what-kinds-link'
                      linkUrl='/find-a-financial-advisor/'
                      ariaLabel='Connect with an advisor to discuss different types of disability insurance costs.'
                      variant='primaryDeemphasize'>
                      Connect with one today
                    </Link>.
                  </Typography>
                </>
              ),
            },
          ]}
        />
      </Col>
    </Row>
  </SectionContainer>
);

